import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, CloudOff, Link2Off, Loader2, Plus, RefreshCw, Search, ShieldAlert } from 'lucide-react'
import { chargerMeta, chercherMateriel, deposerFichier, type Resultat } from './signalements-api.js'
import {
  classesTon, compte, insecable, intentionFichier,
  type Droits, type ErreurSig, type Intentions, type Materiel, type MetaSignalements,
} from './signalements-model.js'
import './signalements.css'

/**
 * SIGNALEMENTS — LES BRIQUES PARTAGÉES DES ÉCRANS (09/10/2026).
 *
 * Technique est la source de vérité : ces composants affichent ce que le
 * serveur de GESTION relaie, sans rien garder au-delà de l'écran. Ils sont
 * construits sur les primitives LIVE CONTROL existantes (`lc-pill`, `lc-dot`,
 * `lc-btn`, `lc-input`, `so-skel`) ; `signalements.css` n'ajoute que la mise
 * en page propre au module, en jetons.
 */

/* ═══════════════════════ le catalogue, une fois par document ═══════════════════════ */

/*
 * `/meta` ne change qu'avec un déploiement de Technique : il est lu une fois
 * par document et gardé EN MÉMOIRE (jamais dans un stockage). Un échec n'est
 * pas retenu : l'écran suivant redemande.
 */
let metaEnMemoire: { meta: MetaSignalements; droits: Droits } | null = null
let metaEnVol: Promise<Resultat<{ meta: MetaSignalements; droits: Droits }>> | null = null

export type EtatMeta =
  | { phase: 'chargement' }
  | { phase: 'ok'; meta: MetaSignalements; droits: Droits }
  | { phase: 'erreur'; erreur: ErreurSig }

export function useMetaSignalements(): { etat: EtatMeta; recharger: () => void } {
  const [etat, setEtat] = useState<EtatMeta>(() => (metaEnMemoire ? { phase: 'ok', ...metaEnMemoire } : { phase: 'chargement' }))
  const [essai, setEssai] = useState(0)
  useEffect(() => {
    if (metaEnMemoire) { setEtat({ phase: 'ok', ...metaEnMemoire }); return }
    let vivant = true
    setEtat({ phase: 'chargement' })
    metaEnVol ??= chargerMeta().finally(() => { metaEnVol = null })
    void metaEnVol.then((r) => {
      if (r.ok) metaEnMemoire = r.data
      if (!vivant) return
      setEtat(r.ok ? { phase: 'ok', ...r.data } : { phase: 'erreur', erreur: r.erreur })
    })
    return () => { vivant = false }
  }, [essai])
  return { etat, recharger: () => setEssai((n) => n + 1) }
}

/* ═══════════════════════ dépôt de pièces jointes ═══════════════════════ */

/**
 * Dépose des fichiers sur un signalement, UN PAR UN (Technique n'accepte que
 * quelques dépôts simultanés pour tout GESTION), chacun avec SA clé
 * d'idempotence : redéposer le même fichier après un échec ne crée pas de
 * doublon. Rien n'est réessayé ici : le bilan dit ce qui a échoué.
 */
export async function deposerFichiers(
  reference: string, fichiers: readonly File[], intentions: Intentions,
): Promise<{ deposes: string[]; echecs: { nom: string; erreur: ErreurSig }[] }> {
  const deposes: string[] = []
  const echecs: { nom: string; erreur: ErreurSig }[] = []
  for (const f of fichiers) {
    const intention = intentionFichier(reference, f)
    const r = await deposerFichier(reference, f, intentions.cle(intention))
    intentions.apres(intention, r.ok ? null : r.erreur)
    if (r.ok) deposes.push(f.name)
    else echecs.push({ nom: f.name, erreur: r.erreur })
  }
  return { deposes, echecs }
}

/* ═══════════════════════ pastilles ═══════════════════════ */

/** Couleur + pastille + texte : le ton vient du catalogue, la couleur de LIVE CONTROL. */
export function Pastille(props: { ton: string; label: string; titre?: string | undefined }) {
  const c = classesTon(props.ton)
  return (
    <span className={`${c.pill} sig-pastille`} title={props.titre}>
      <span className={c.dot} aria-hidden="true" />
      {props.label}
    </span>
  )
}

/** La pastille seule, quand le libellé est déjà écrit à côté. */
export function Point({ ton }: { ton: string }) {
  return <span className={classesTon(ton).dot} aria-hidden="true" />
}

/* ═══════════════════════ erreurs et états ═══════════════════════ */

/** L'identifiant de requête GESTION, discret : il retrouve la trace côté serveur. */
export function Trace({ erreur }: { erreur: ErreurSig | null }) {
  if (!erreur?.traceId) return null
  return <span className="sig-trace" title={`Identifiant de la requête : ${erreur.traceId}`}>Réf. {erreur.traceId.slice(0, 8)}</span>
}

/**
 * Un état d'erreur dit selon sa CAUSE :
 *  · Technique absent (503 / non raccordé) → l'état dédié du domaine : le reste
 *    de GESTION continue, aucun nouvel essai automatique ;
 *  · raccordement refusé ou réponse hors contrat (502) → une erreur
 *    d'intégration, jamais « vous n'avez pas le droit » ;
 *  · droit GESTION manquant (403) → le droit, et seulement lui.
 */
export function EtatErreur(props: { erreur: ErreurSig; quoi?: string; onReessayer?: (() => void) | undefined; compact?: boolean }) {
  const e = props.erreur
  const indispo = e.genre === 'indisponible' || e.genre === 'non_configure'
  const Icone = indispo ? CloudOff : e.genre === 'interdit' ? ShieldAlert : e.genre === 'introuvable' ? Link2Off : AlertTriangle
  // La phrase du contrat se lit d'un trait : son début devient le titre, sa suite le message.
  const titre = indispo && e.genre === 'indisponible' ? 'Technique momentanément indisponible.'
    : indispo ? 'Technique non raccordé'
    : e.genre === 'integration' ? (e.code === 'TECHNIQUE_INVALID_RESPONSE' ? 'Réponse de Technique illisible' : 'Raccordement à Technique refusé')
      : e.genre === 'interdit' ? 'Accès refusé par GESTION'
        : e.genre === 'introuvable' ? 'Introuvable'
          : props.quoi ? `Impossible de charger ${props.quoi}` : 'La demande a échoué'
  return (
    <div className={`sig-etat${indispo ? ' sig-etat--indispo' : ''}${props.compact ? ' sig-etat--compact' : ''}`} role="alert">
      <Icone size={20} aria-hidden="true" className="sig-etat__icone" />
      <div className="sig-etat__texte">
        <p className="sig-etat__titre">{titre}</p>
        <p className="sig-etat__message">{e.genre === 'indisponible' ? e.message.replace(/^Technique momentanément indisponible\.\s*/, '') : e.message}</p>
        <Trace erreur={e} />
      </div>
      {/* Réessayer ne change rien à un droit manquant, ni à un raccordement absent (configuration du serveur). */}
      {props.onReessayer && e.genre !== 'interdit' && e.genre !== 'non_configure' ? (
        <button type="button" className="lc-btn lc-btn--sm" onClick={props.onReessayer}>
          <RefreshCw size={14} aria-hidden="true" /> Réessayer
        </button>
      ) : null}
    </div>
  )
}

/**
 * Attente sobre : le squelette du socle (`so-skel`), aucune classe inventée.
 * Annoncée aux lecteurs d'écran : une région `status` qui porte son libellé
 * en TEXTE (masqué à l'écran, `u-sr`). Un `aria-label` sur un bloc sans rôle
 * n'est pas lu, et `aria-busy` sur une région vivante en retient l'annonce.
 */
export function Chargement({ libelle }: { libelle: string }) {
  return (
    <div className="sig-chargement" role="status" aria-live="polite">
      <span className="u-sr">{libelle}…</span>
      <div className="so-skel" aria-hidden="true"><span /><span /><span /></div>
    </div>
  )
}

/** Une ligne de faits `dt`/`dd`, valeur absente écrite « — ». */
export function Fait(props: { terme: string; children: ReactNode }) {
  return (
    <div className="sig-fait">
      <dt>{props.terme}</dt>
      <dd>{props.children ?? '—'}</dd>
    </div>
  )
}

/* ═══════════════════════ champ de texte long ═══════════════════════ */

export function ZoneTexte(props: {
  id?: string
  value: string
  onChange: (v: string) => void
  maxLength?: number
  rows?: number
  placeholder?: string
  ariaLabel?: string
  invalide?: boolean
  autoFocus?: boolean
  disabled?: boolean
}) {
  return (
    <textarea
      id={props.id}
      className={`lc-input sig-zone${props.invalide ? ' lc-input--error' : ''}`}
      value={props.value}
      rows={props.rows ?? 4}
      maxLength={props.maxLength}
      placeholder={props.placeholder}
      aria-label={props.ariaLabel}
      aria-invalid={props.invalide || undefined}
      autoFocus={props.autoFocus}
      disabled={props.disabled}
      onChange={(e) => props.onChange(e.target.value)}
    />
  )
}

/* ═══════════════════════ recherche de matériel (Parc de Technique) ═══════════════════════ */

/**
 * Recherche ASYNCHRONE dans le Parc, par le BFF. Temporisée (350 ms) et
 * déclenchée à partir de deux caractères : le quota de lecture du Parc est
 * partagé par tous les utilisateurs de GESTION. Chaque résultat montre ce qui
 * permet de choisir le BON appareil : numéro d'inventaire, nom, modèle, salle,
 * état et disponibilité.
 */
export function RechercheMateriel(props: {
  onChoisir: (m: Materiel) => void
  /** Identifiants déjà retenus : affichés comme tels, non rechoisissables. */
  deja: readonly string[]
  occupe?: boolean
}) {
  const [q, setQ] = useState('')
  const [resultats, setResultats] = useState<Materiel[]>([])
  const [curseur, setCurseur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<ErreurSig | null>(null)
  const generation = useRef(0)

  useEffect(() => {
    const terme = q.trim()
    generation.current += 1
    const g = generation.current
    if (terme.length < 2) { setResultats([]); setCurseur(null); setErreur(null); setEnCours(false); return }
    setEnCours(true)
    const t = window.setTimeout(() => {
      void chercherMateriel(terme, null).then((r) => {
        if (g !== generation.current) return
        setEnCours(false)
        if (r.ok) { setResultats(r.data.items); setCurseur(r.data.next_cursor); setErreur(null) }
        else { setResultats([]); setCurseur(null); setErreur(r.erreur) }
      })
    }, 350)
    return () => window.clearTimeout(t)
  }, [q])

  const suite = async () => {
    if (!curseur) return
    const g = generation.current
    setEnCours(true)
    const r = await chercherMateriel(q.trim(), curseur)
    if (g !== generation.current) return
    setEnCours(false)
    if (r.ok) { setResultats((x) => [...x, ...r.data.items.filter((m) => !x.some((y) => y.id === m.id))]); setCurseur(r.data.next_cursor) }
    else setErreur(r.erreur)
  }

  return (
    <div className="sig-materiel">
      <label className="lc-search sig-materiel__recherche">
        <Search size={16} className="lc-search__icon" aria-hidden="true" />
        <input
          type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Nom, numéro d’inventaire, marque, modèle…"
          aria-label="Rechercher un matériel du Parc" maxLength={100}
        />
      </label>
      <p className="sig-aide" aria-live="polite">
        {q.trim().length < 2 ? 'Tapez au moins deux caractères.'
          : enCours ? 'Recherche dans le Parc…'
            : erreur ? null
              : resultats.length === 0 ? 'Aucun matériel ne correspond.' : compte(resultats.length, 'résultat', 'résultats') + (curseur ? ' (et d’autres)' : '')}
      </p>
      {erreur ? <EtatErreur erreur={erreur} quoi="le Parc" compact /> : null}
      {resultats.length > 0 ? (
        <ul className="sig-materiel__liste">
          {resultats.map((m) => {
            const pris = props.deja.includes(String(m.id))
            return (
              <li key={m.id}>
                <button type="button" className="sig-materiel__choix" disabled={pris || props.occupe}
                  onClick={() => props.onChoisir(m)} aria-label={`${pris ? 'Déjà lié' : 'Lier'} : ${m.nom}${m.numero_inventaire ? `, n° ${m.numero_inventaire}` : ''}`}>
                  <LigneMateriel m={m} />
                  <span className="sig-materiel__geste">{pris ? 'Déjà lié' : <><Plus size={14} aria-hidden="true" /> Lier</>}</span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
      {curseur && !enCours ? (
        <button type="button" className="lc-btn lc-btn--sm lc-btn--ghost" onClick={() => void suite()}>Afficher plus de matériel</button>
      ) : null}
      {enCours && resultats.length > 0 ? <Loader2 size={16} className="lc-spin" aria-hidden="true" /> : null}
    </div>
  )
}

/** Un matériel en une ligne : de quoi le reconnaître sans ouvrir le Parc. */
export function LigneMateriel({ m }: { m: Materiel }) {
  const modele = [m.marque, m.modele].filter(Boolean).join(' ')
  return (
    <span className="sig-materiel__ligne">
      <span className="sig-materiel__nom">
        {m.numero_inventaire ? <span className="sig-ref">{m.numero_inventaire}</span> : null}
        <strong>{m.nom}</strong>
      </span>
      <span className="sig-materiel__meta">
        {[modele || null, m.salle ? insecable(m.salle) : null, m.etat ? insecable(m.etat) : null].filter(Boolean).join(' · ') || '—'}
        {m.disponible === null ? null : (
          <span className={m.disponible ? 'lc-pill lc-pill--ok sig-pastille' : 'lc-pill lc-pill--warning sig-pastille'}>
            {m.disponible ? 'Disponible' : 'Indisponible'}
          </span>
        )}
      </span>
    </span>
  )
}
